import { useState, useRef } from "react";
import { ArrowLeft, UserPlus, Trash2, Play, Copy, Check, Eye, EyeOff, ChevronUp, ChevronDown, GripVertical, Palette, Volume2, VolumeX, Layout, HelpCircle, Edit2, Share2, PenLine, Dice5 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SessionState, ScoreVisibility, GameMode, User } from "../../types/game";
import AddPlayerModal from "./AddPlayerModal";
import FontScaler from "../common/FontScaler";
import LanguageSwitcher from "../common/LanguageSwitcher";
import Avatar from "../common/Avatar";
import { QRCodeSVG } from "qrcode.react";
import WebApp from "@twa-dev/sdk";
import { formatGameDate } from "../../utils/date";
import { cyrillicCode } from "../../utils/inviteCode";
import { TABLE_THEMES } from "../../utils/themes";
import RulesModal from "../common/RulesModal";

interface Props {
  state: SessionState;
  token: string;
  myUserId: string | null;
  myTelegramId: number | null;
  colorfulDice?: boolean;
  soundEnabled?: boolean;
  onAddReal: (user_id: string, display_name?: string) => void;
  onAddPhantom: (display_name: string, avatar: string) => void;
  onRemovePlayer: (player_id: string) => void;
  onUpdateSettings: (sv?: ScoreVisibility, gm?: GameMode) => void;
  onToggleColorful?: (val: boolean) => void;
  onToggleSound?: (val: boolean) => void;
  onToggleTheme?: (val: string) => void;
  onUpdateUser?: (data: Partial<User>) => void;
  onStartGame: () => void;
  onReorderPlayers: (orderedPlayerIds: string[]) => void;
  onUpdatePlayerName?: (playerId: string, name: string) => void;
  onLeave?: () => void;
  themeId?: string;
}

export default function SetupView({
  state,
  token,
  myUserId,
  colorfulDice = false,
  soundEnabled = true,
  themeId = "classic",
  onAddReal,
  onAddPhantom,
  onRemovePlayer,
  onUpdateSettings,
  onToggleColorful,
  onToggleSound,
  onToggleTheme,
  onStartGame,
  onReorderPlayers,
  onUpdatePlayerName,
  onLeave,
}: Props) {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const isAdmin = state.session.created_by === myUserId;
  const sv = state.session.score_visibility ?? "realtime";

  // Drag state
  const dragIndexRef = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const botUsername = import.meta.env.VITE_BOT_USERNAME || "vltvch_test_bot";
  
  // Use Telegram deep link when running inside Telegram, otherwise use web URL
  const isTelegram = !!(WebApp.initData?.trim() || (typeof window !== "undefined" && (window as any).Telegram?.WebApp?.initData));
  const inviteUrl = isTelegram
    ? `https://t.me/${botUsername}?startapp=${state.session.invite_code}`
    : `${window.location.origin}?code=${state.session.invite_code}`;

  const copyCode = async () => {
    await navigator.clipboard.writeText(state.session.invite_code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const players = state.players_with_scorecards || [];

  // Move a player up or down in the order
  const move = (index: number, dir: -1 | 1) => {
    const newOrder = players.map(p => p.player.id);
    const target = index + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    onReorderPlayers(newOrder);
  };

  const handleStartEdit = (p: { id: string, display_name: string }) => {
    if (!isAdmin) return;
    setEditingId(p.id);
    setEditName(p.display_name);
  };

  const handleSaveEdit = () => {
    if (editingId && editName.trim()) {
      onUpdatePlayerName?.(editingId, editName.trim());
    }
    setEditingId(null);
  };

  // HTML5 drag handlers
  const onDragStart = (index: number) => { dragIndexRef.current = index; };
  const onDragEnter = (index: number) => setDragOver(index);
  const onDragEnd = () => {
    if (dragIndexRef.current !== null && dragOver !== null && dragIndexRef.current !== dragOver) {
      const newOrder = players.map(p => p.player.id);
      const [moved] = newOrder.splice(dragIndexRef.current, 1);
      newOrder.splice(dragOver, 0, moved);
      onReorderPlayers(newOrder);
    }
    dragIndexRef.current = null;
    setDragOver(null);
  };

  return (
    <div className="min-h-screen text-ink flex flex-col p-4">
      <div className="max-w-md mx-auto w-full flex flex-col flex-1">
        {/* Top bar */}
        <div className="flex justify-between pt-3 pb-1">
          <button
            onClick={onLeave}
            className="p-2 -ml-2 text-ink/50 hover:text-ink transition-colors flex items-center gap-1"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">{t("setup.backToLobby")}</span>
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => setShowRules(true)}
              className="p-2 text-ink/50 hover:text-ink transition-colors"
              title={t("lobby.rules", "Rules")}
            >
              <HelpCircle size={18} />
            </button>
            <FontScaler />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Header */}
        <div className="py-4 text-center">
          <div className="text-4xl mb-2 select-none">🎲</div>
          <h2 className="text-xl font-bold">
            {formatGameDate(state.session.created_at, i18n.language)}
          </h2>
          <p className="text-ink/40 text-sm mt-1">{t("setup.subtitle")}</p>
        </div>

        {/* Invite code & QR */}
        {state.session.invite_code !== "LOCAL" && (
          <div className="space-y-3 mb-6">
            <div className="glass rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-ink/35 font-black uppercase tracking-widest mb-1">{t("setup.gameCode")}</p>
                <p className="font-mono text-2xl font-bold tracking-widest text-blue-400">
                  {state.session.invite_code}
                </p>
                {cyrillicCode(state.session.invite_code) !== state.session.invite_code && (
                  <p className="font-mono text-sm font-bold tracking-widest text-blue-400/60 mt-0.5">
                    🇷🇺 {cyrillicCode(state.session.invite_code)}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    const msg = t("setup.inviteMessage", { code: state.session.invite_code });
                    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(msg)}`;
                    try {
                      WebApp.openTelegramLink(shareUrl);
                    } catch {
                      navigator.clipboard?.writeText(inviteUrl);
                    }
                  }}
                  className="p-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-600/20"
                  title={t("setup.shareInvite")}
                >
                  <Share2 size={20} />
                </button>
                <button onClick={copyCode} className="p-3 glass-btn rounded-xl transition-all active:scale-95">
                  {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} />}
                </button>
              </div>
            </div>

            <div className="glass rounded-2xl p-5 flex flex-col items-center justify-center">
              <div className="bg-white p-3 rounded-2xl flex items-center justify-center shadow-2xl">
                <QRCodeSVG value={inviteUrl} size={160} level="H" fgColor="#000000" bgColor="#ffffff" />
              </div>
              <p className="text-ink/40 text-xs mt-4 text-center font-bold uppercase tracking-wider">
                {t("setup.qrScan")}
              </p>
            </div>
          </div>
        )}

        {/* Player List */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="text-[10px] font-black text-ink/35 uppercase tracking-[0.2em]">
              {t("setup.players")} ({players.length}/6)
            </h3>
            {isAdmin && players.length < 6 && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition-colors text-sm font-black uppercase tracking-tight"
              >
                <UserPlus size={16} strokeWidth={3} />
                {t("setup.addPlayer")}
              </button>
            )}
          </div>

          <div className="space-y-2">
            {players.map((pwsc, index) => {
              const p = pwsc.player;
              const isMe = p.user_id === myUserId;
              const canRemove = isAdmin && !p.is_admin;
              
              return (
                <div
                  key={p.id}
                  draggable={isAdmin}
                  onDragStart={() => onDragStart(index)}
                  onDragEnter={() => onDragEnter(index)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  className={`flex items-center gap-3 p-3.5 glass rounded-2xl border transition-all ${
                    dragOver === index ? "border-blue-400/60 scale-[1.02] bg-ink/10" : "border-transparent"
                  } ${isMe ? "ring-2 ring-blue-500/50" : ""}`}
                >
                  {isAdmin && (
                    <div className="text-ink/25 cursor-grab active:cursor-grabbing px-1">
                      <GripVertical size={20} />
                    </div>
                  )}
                  
                  <Avatar src={p.avatar} size="md" />
                  
                  <div className="flex-1 min-w-0">
                    {editingId === p.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={handleSaveEdit}
                        onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
                        className="w-full glass-input border-2 border-blue-500 rounded-xl px-3 py-1.5 text-sm font-bold text-ink outline-none"
                      />
                    ) : (
                      <div 
                        className={`flex items-center gap-2 ${isAdmin ? "cursor-pointer group" : ""}`}
                        onClick={() => handleStartEdit(p)}
                      >
                        <p className="font-bold truncate text-base">{p.display_name}</p>
                        {isAdmin && <Edit2 size={14} className="text-ink/40 group-hover:text-blue-400 transition-colors" />}
                        {p.is_admin && (
                          <span className="text-[9px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full font-black uppercase tracking-tighter border border-blue-500/30">
                            {t("setup.host")}
                          </span>
                        )}
                        {isMe && (
                          <span className="text-[9px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-black uppercase tracking-tighter border border-green-500/30">
                            {t("setup.you")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {isAdmin && (
                      <div className="flex flex-col">
                        <button 
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          className="p-1 text-ink/35 hover:text-ink disabled:opacity-0 transition-colors"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => move(index, 1)}
                          disabled={index === players.length - 1}
                          className="p-1 text-ink/35 hover:text-ink disabled:opacity-0 transition-colors"
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>
                    )}
                    
                    {canRemove && (
                      <button
                        onClick={() => onRemovePlayer(p.id)}
                        className="p-3 text-ink/35 hover:text-red-400 transition-colors glass rounded-xl"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settings & Actions */}
        <div className="mt-8 space-y-4 pb-8">
          {isAdmin && (
            <div className="glass-strong rounded-3xl p-5 space-y-5">

              {/* Score Visibility */}
              <div>
                <label className="text-[10px] text-ink/35 font-black uppercase tracking-widest mb-2.5 block px-1">
                  {t("setup.visibilityOfScore")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onUpdateSettings("realtime")}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-2xl border-2 transition-all active:scale-[0.97] text-left ${
                      sv === "realtime"
                        ? "bg-blue-500/15 border-blue-500/50"
                        : "glass border-transparent hover:bg-ink/5"
                    }`}
                  >
                    <Eye size={20} className={sv === "realtime" ? "text-blue-400" : "text-ink/30"} />
                    <span className={`text-sm font-bold leading-tight ${sv === "realtime" ? "text-ink" : "text-ink/50"}`}>
                      {t("setup.realtime")}
                    </span>
                    <span className={`text-[11px] leading-snug ${sv === "realtime" ? "text-ink/55" : "text-ink/25"}`}>
                      {t("setup.realtimeDesc")}
                    </span>
                  </button>
                  <button
                    onClick={() => onUpdateSettings("final_only")}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-2xl border-2 transition-all active:scale-[0.97] text-left ${
                      sv === "final_only"
                        ? "bg-orange-500/15 border-orange-500/50"
                        : "glass border-transparent hover:bg-ink/5"
                    }`}
                  >
                    <EyeOff size={20} className={sv === "final_only" ? "text-orange-400" : "text-ink/30"} />
                    <span className={`text-sm font-bold leading-tight ${sv === "final_only" ? "text-ink" : "text-ink/50"}`}>
                      {t("setup.finalOnly")}
                    </span>
                    <span className={`text-[11px] leading-snug ${sv === "final_only" ? "text-ink/55" : "text-ink/25"}`}>
                      {t("setup.finalOnlyDesc")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Game Mode */}
              <div>
                <label className="text-[10px] text-ink/35 font-black uppercase tracking-widest mb-2.5 block px-1">
                  {t("setup.gameMode")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onUpdateSettings(undefined, "free")}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-2xl border-2 transition-all active:scale-[0.97] text-left ${
                      state.session.game_mode === "free"
                        ? "bg-green-500/15 border-green-500/50"
                        : "glass border-transparent hover:bg-ink/5"
                    }`}
                  >
                    <PenLine size={20} className={state.session.game_mode === "free" ? "text-green-400" : "text-ink/30"} />
                    <span className={`text-sm font-bold leading-tight ${state.session.game_mode === "free" ? "text-ink" : "text-ink/50"}`}>
                      {t("setup.freeMode")}
                    </span>
                    <span className={`text-[11px] leading-snug ${state.session.game_mode === "free" ? "text-ink/55" : "text-ink/25"}`}>
                      {t("setup.freeModeDesc")}
                    </span>
                  </button>
                  <button
                    onClick={() => onUpdateSettings(undefined, "strict")}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-2xl border-2 transition-all active:scale-[0.97] text-left ${
                      state.session.game_mode === "strict"
                        ? "bg-purple-500/15 border-purple-500/50"
                        : "glass border-transparent hover:bg-ink/5"
                    }`}
                  >
                    <Dice5 size={20} className={state.session.game_mode === "strict" ? "text-purple-400" : "text-ink/30"} />
                    <span className={`text-sm font-bold leading-tight ${state.session.game_mode === "strict" ? "text-ink" : "text-ink/50"}`}>
                      {t("setup.virtualDiceMode")}
                    </span>
                    <span className={`text-[11px] leading-snug ${state.session.game_mode === "strict" ? "text-ink/55" : "text-ink/25"}`}>
                      {t("setup.virtualDiceModeDesc")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Personal Settings */}
              <div className={`grid gap-2 pt-4 border-t border-ink/[0.08] ${state.session.game_mode === "strict" ? "grid-cols-3" : "grid-cols-2"}`}>
                <button
                  onClick={() => onToggleSound?.(!soundEnabled)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all active:scale-95 ${
                    soundEnabled ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "glass text-ink/35 border border-transparent"
                  }`}
                >
                  {soundEnabled ? <Volume2 size={24} /> : <VolumeX size={24} />}
                  <span className="text-[9px] font-black uppercase tracking-tighter">{t("settings.sound")}</span>
                </button>
                {state.session.game_mode === "strict" && (
                  <button
                    onClick={() => onToggleColorful?.(!colorfulDice)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all active:scale-95 ${
                      colorfulDice ? "bg-purple-600/20 text-purple-400 border border-purple-500/30" : "glass text-ink/35 border border-transparent"
                    }`}
                  >
                    <Palette size={24} />
                    <span className="text-[9px] font-black uppercase tracking-tighter">{t("settings.colorfulDice")}</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    const currentIndex = TABLE_THEMES.findIndex(th => th.id === themeId);
                    const nextTheme = TABLE_THEMES[(currentIndex + 1) % TABLE_THEMES.length];
                    onToggleTheme?.(nextTheme.id);
                  }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all active:scale-95 ${
                    themeId && themeId !== "classic"
                      ? "bg-purple-600/20 text-purple-400 border border-purple-500/30"
                      : "glass text-ink/35 border border-transparent"
                  }`}
                >
                  <Layout size={24} />
                  <span className="text-[9px] font-black uppercase tracking-tighter">
                    {TABLE_THEMES.find(th => th.id === themeId)?.name_ru ?? t("settings.theme")}
                  </span>
                </button>
              </div>
            </div>
          )}

          {isAdmin ? (
            <button
              onClick={onStartGame}
              disabled={players.length < 1}
              className="w-full py-5 rounded-3xl font-black text-2xl text-white transition-all flex items-center justify-center gap-4 active:scale-[0.98] disabled:opacity-40"
              style={{
                background: "linear-gradient(135deg, rgba(59,130,246,0.9), rgba(99,102,241,0.9))",
                boxShadow: "0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              <Play size={28} fill="currentColor" />
              {t("setup.startGame")}
            </button>
          ) : (
            <div className="text-center py-10 glass rounded-[2.5rem] border-2 border-dashed border-ink/10">
              <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-ink/40 font-bold uppercase tracking-widest text-xs">{t("setup.waiting")}</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <AddPlayerModal
          token={token}
          onAddReal={onAddReal}
          onAddPhantom={onAddPhantom}
          onClose={() => setShowModal(false)}
          excludeNames={players.map(pwsc => pwsc.player.display_name)}
        />
      )}
      {showRules && <RulesModal onClose={() => setShowRules(false)} />}
    </div>
  );
}
